import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { JoinReportCard } from '@/components/CoincidenceCard';
import { PrzyciskDowodu } from '@/components/SzufladaDowodu';
import type { JoinReport } from '@/lib/crossSystem';
import { dowodZlaczenia } from './sygnaly';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WIARYGODNOŚĆ ZŁĄCZENIA — widoczna ZAWSZE, a zwłaszcza wtedy, gdy złączenia nie ma
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * DEFEKT, KTÓRY TO NAPRAWIA: dotąd raport złączenia renderował się wyłącznie wtedy, gdy złączenie
 * się udało. Czyli dokładnie ten element, który pozwala odróżnić „nie ma sygnału" od „nie da się
 * policzyć", znikał w jedynym przypadku, w którym był potrzebny. Ekran bez dostaw wyglądał wtedy
 * jak ekran ze spokojnymi dostawami.
 *
 * Wariant „nie policzono" NIE pokazuje zer. Pokazuje kreski i powód — bo zero dopasowanych szkół
 * przy policzonym złączeniu i brak policzonego złączenia to dwa różne stany świata.
 */

function WierszPusty({ etykieta }: { etykieta: string }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
        {etykieta}
      </Typography>
      <Typography variant="caption" className="sygnal-liczba" sx={{ color: 'var(--tekst-slaby)' }}>
        —
      </Typography>
    </Stack>
  );
}

export function WiarygodnoscZlaczenia({
  report,
  powodBraku,
  okno,
  porownanie,
  kartaId,
}: {
  /** Raport złączenia albo null, gdy złączenia w ogóle nie policzono. */
  report: JoinReport | null;
  powodBraku: string;
  okno: string;
  porownanie: string;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. „Wartość wg cennika" występuje
   * cztery razy na jednym ekranie, „Zamówienia w oknie" w sześciu plikach, a „przed czasem"
   * powtarza się raz na każdą szkołę w rankingu. Alternatywą było kotwiczenie po klasach MUI
   * i wartościach stylów — narzędzie, które działa dziś i zaczyna cicho kłamać po pierwszym
   * refaktorze wyglądu.
   *
   * Atrybut siedzi na korzeniu OBU wariantów — także tego bez policzonego złączenia. Gdyby
   * jechał tylko z raportem, karta znikałaby z adresowania dokładnie w tym stanie, dla którego
   * ten komponent powstał, a walidacja czytałaby „nie policzono" jako „nie ma karty".
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  if (report) {
    return (
      <Box data-cc-card-id={kartaId}>
        <JoinReportCard report={report} />
        <Box sx={{ mt: 1, ml: -1 }}>
          <PrzyciskDowodu dowod={dowodZlaczenia(report, okno, porownanie)} etykieta="Skąd te liczby" />
        </Box>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="subtitle2">Wiarygodność złączenia</Typography>
        <Chip size="small" color="default" variant="outlined" label="NIE POLICZONO" />
      </Stack>

      <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 1.5 }}>
        {powodBraku}
      </Typography>

      {/* Kreski, nie zera. Zero dopasowanych szkół jest wynikiem pomiaru; brak pomiaru nim nie jest. */}
      <WierszPusty etykieta="szkoły ze sprzedażą" />
      <WierszPusty etykieta="szkoły z dostawami" />
      <WierszPusty etykieta="dopasowane" />
      <WierszPusty etykieta="tylko sprzedaż" />
      <WierszPusty etykieta="tylko dostawy" />
      <WierszPusty etykieta="dostawy bez identyfikatora szkoły" />

      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 1.5 }}>
        Okno: {okno} · porównanie: {porownanie}
      </Typography>
    </Paper>
  );
}
